/**
 * EFormBuilder — corp's Form Builder (corporate-dashboard
 * components/form-builder.tsx) for one account, with corp's look and drag:
 *   - the header: the form's title, the change summary ("2 modified · 1 added
 *     · 1 removed · order changed") with Revert, the errors badge (hover for
 *     the list), Preview (the form's direct link, opened on this app's own
 *     host: see previewLinkOnThisHost) and Save All;
 *   - left, the palette ("Drag to Add"): drag a field type onto the canvas;
 *   - centre, the questions in order as cards drawn like the form; drag a card
 *     to reorder. While dragging, a blue line shows where the drop lands, and
 *     the canvas scrolls near its edges. Letting go anywhere off the canvas
 *     (back on the palette) cancels. A card's trash removes the question
 *     (saved with Save All);
 *   - right, EFormFieldProperties for the chosen question.
 * The drag is the browser's own, as corp's is.
 * Save All writes the edited option groups, then every question at once;
 * questions left out are deleted. Logic is part of each question, so its edits
 * count as changes (corp tracked neither them nor logic on new questions).
 * Removing a question also removes the rules that tested it.
 *
 * On a Locked E-Forms page (plan 2026-10-10-001, U7) the builder only shows the
 * form: the canvas draws its questions and a click shows a question's
 * properties to read (EFormFieldProperties in a locked scope), but there is no
 * palette, drag, remove, revert or Save All, and every edit path refuses.
 * Nothing says so (R18).
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type DragEvent as ReactDragEvent,
  type KeyboardEvent,
  type MutableRefObject,
} from 'react';
import {
  AlertTriangle,
  Calendar,
  ChevronDown,
  ChevronRight,
  Eye,
  FileUp,
  GripVertical,
  Loader2,
  PenTool,
  RotateCcw,
  Save,
  StickyNote,
  Trash2,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Modal } from '@/components/ui/Modal';
import { cn } from '@/lib/utils';
import { getErrorMessage } from '@/services/apiClient';
import {
  ADDRESS,
  CONTENT_TYPES,
  CREDIT_CARD,
  PAGE_BREAK,
  QUESTION_GROUPS,
  QUESTION_TYPES,
  SharedOptionGroupError,
  fieldErrors,
  fieldOptions,
  listDemographicFields,
  loadBuilder,
  newBuilderField,
  plainText,
  questionTypeLabel,
  saveBuilder,
  type BuilderField,
  type DemographicField,
  type EForm,
  type OptionGroup,
  type QuestionGroup,
} from '@/services/eformsAdmin.service';
import { listMerchants, type Merchant } from '@/services/payments.service';
import { toast } from '@/store/toastStore';
import { AdminLockedFieldset } from './AdminLockedFieldset';
import { useAdminPageLevel } from './AdminPageLevelContext';
import { OutlineBadge } from './CorpBadges';
import { EFormFieldProperties, RICH_TEXT, RICH_TEXT_BLOCK } from './EFormFieldProperties';
import { QuestionTypeIcon } from './EFormQuestionTypeIcon';

/** What a drag carries, so Firefox starts it; nothing reads it back. */
const DRAG_TYPE = 'application/x-eform-field';

interface Snapshot {
  fields: BuilderField[];
  json: Map<string, string>;
  order: string[];
}

/** The fields are plain data. */
function clone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

function takeSnapshot(fields: BuilderField[]): Snapshot {
  return { fields: clone(fields), json: new Map(fields.map((f) => [f.key, JSON.stringify(f)])), order: fields.map((f) => f.key) };
}

/**
 * The form's direct link, moved onto the host this app is served from. The API
 * builds the link on the production e-form host (legacy.eform_base_url), which
 * corp-dashboard needs because it runs on a domain of its own. Prospera serves
 * /eform itself, so Preview opens the form on the same site: a PR preview,
 * staging or a local copy shows its own form instead of production's.
 */
export function previewLinkOnThisHost(directLink: string): string {
  try {
    const url = new URL(directLink);
    return `${window.location.origin}${url.pathname}${url.search}${url.hash}`;
  } catch {
    return directLink;
  }
}

function move<T>(list: T[], from: number, to: number): T[] {
  const next = [...list];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
}

export function EFormBuilder({ form, onClose, onSaved }: { form: EForm; onClose: () => void; onSaved?: () => void }) {
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [fields, setFields] = useState<BuilderField[]>([]);
  const [groups, setGroups] = useState<QuestionGroup[]>([]);
  const [optionGroups, setOptionGroups] = useState<OptionGroup[]>([]);
  const [demographics, setDemographics] = useState<DemographicField[]>([]);
  const [merchants, setMerchants] = useState<Merchant[]>([]);
  const [snapshot, setSnapshot] = useState<Snapshot>(() => takeSnapshot([]));
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [confirmClose, setConfirmClose] = useState(false);
  const loadSeq = useRef(0);
  const locked = useAdminPageLevel() === 'locked';

  const load = useCallback(async () => {
    const seq = ++loadSeq.current;
    setLoading(true);
    try {
      const [demos, merch] = await Promise.all([
        listDemographicFields().catch(() => [] as DemographicField[]),
        listMerchants().catch(() => [] as Merchant[]),
      ]);
      const data = await loadBuilder(form.id, demos);
      if (seq !== loadSeq.current) return;
      setDemographics(demos);
      setMerchants(Array.isArray(merch) ? merch : []);
      setFields(data.fields);
      setGroups(data.groups);
      setOptionGroups(data.optionGroups);
      setSnapshot(takeSnapshot(data.fields));
      setLoadFailed(false);
    } catch (err) {
      if (seq !== loadSeq.current) return;
      setLoadFailed(true);
      toast.error(getErrorMessage(err, 'Failed to load survey questions'));
    } finally {
      if (seq === loadSeq.current) setLoading(false);
    }
  }, [form.id]);

  useEffect(() => {
    void load();
  }, [load]);

  // ── Changes ───────────────────────────────────────────────────────────────────
  const changes = useMemo(() => {
    const modified = new Set<string>();
    const added = new Set<string>();
    for (const f of fields) {
      const before = snapshot.json.get(f.key);
      if (before === undefined) added.add(f.key);
      else if (before !== JSON.stringify(f)) modified.add(f.key);
    }
    const keys = new Set(fields.map((f) => f.key));
    const removed = snapshot.order.filter((k) => !keys.has(k)).length;
    const surviving = snapshot.order.filter((k) => keys.has(k));
    const now = fields.map((f) => f.key).filter((k) => snapshot.json.has(k));
    const orderChanged = surviving.some((k, i) => now[i] !== k);
    return { modified, added, removed, orderChanged };
  }, [fields, snapshot]);
  const dirty = changes.modified.size > 0 || changes.added.size > 0 || changes.removed > 0 || changes.orderChanged;
  const summary = [
    changes.modified.size > 0 && `${changes.modified.size} modified`,
    changes.added.size > 0 && `${changes.added.size} added`,
    changes.removed > 0 && `${changes.removed} removed`,
    changes.orderChanged && 'order changed',
  ]
    .filter(Boolean)
    .join(' · ');

  const errorsByKey = useMemo(() => new Map(fields.map((f) => [f.key, fieldErrors(f, optionGroups)])), [fields, optionGroups]);
  const errors = fields.flatMap((f, i) => (errorsByKey.get(f.key) ?? []).map((m) => `#${i + 1} ${questionTypeLabel(f.type)}: ${m}`));

  // ── Edits ─────────────────────────────────────────────────────────────────────
  const updateField = (next: BuilderField) => {
    if (!locked) setFields((prev) => prev.map((f) => (f.key === next.key ? next : f)));
  };

  function insertField(type: number, index: number) {
    if (locked) return;
    const field = newBuilderField(type);
    setFields((prev) => {
      const at = Math.max(0, Math.min(index, prev.length));
      return [...prev.slice(0, at), field, ...prev.slice(at)];
    });
    setSelectedKey(field.key);
  }

  function removeField(key: string) {
    if (locked) return;
    const gone = fields.find((f) => f.key === key);
    setFields((prev) =>
      prev
        .filter((f) => f.key !== key)
        // Rules that tested the removed question go with it.
        .map((f) => (gone?.orderId && f.rules.some((r) => r.targetOrderId === gone.orderId) ? { ...f, rules: f.rules.filter((r) => r.targetOrderId !== gone.orderId) } : f)),
    );
    if (selectedKey === key) setSelectedKey(null);
  }

  function revertField(key: string) {
    const before = snapshot.fields.find((f) => f.key === key);
    if (before) updateField(clone(before));
  }

  function revertAll() {
    setFields(clone(snapshot.fields));
    if (selectedKey && !snapshot.json.has(selectedKey)) setSelectedKey(null);
  }

  // ── Drag (corp's: the browser's drag and drop, drop line between cards) ─────────
  const [draggedKey, setDraggedKey] = useState<string | null>(null);
  const [draggedType, setDraggedType] = useState<number | null>(null);
  const [dropIndex, setDropIndex] = useState<number | null>(null);
  const isDragging = draggedKey !== null || draggedType !== null;
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);
  const canvasRef = useRef<HTMLDivElement | null>(null);
  const scrollRaf = useRef<number | null>(null);

  const stopScroll = useCallback(() => {
    if (scrollRaf.current) cancelAnimationFrame(scrollRaf.current);
    scrollRaf.current = null;
  }, []);

  useEffect(() => stopScroll, [stopScroll]);

  function handlePaletteDragStart(e: ReactDragEvent<HTMLDivElement>, type: number) {
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = 'copy';
      e.dataTransfer.setData(DRAG_TYPE, String(type));
    }
    setDraggedType(type);
  }

  function handleCardDragStart(e: ReactDragEvent<HTMLDivElement>, key: string) {
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData(DRAG_TYPE, key);
    }
    setDraggedKey(key);
  }

  function handleCanvasDragOver(e: ReactDragEvent<HTMLDivElement>) {
    e.preventDefault();
    if (!isDragging) return;

    // The drop goes before the first card whose middle is below the pointer.
    const mouseY = e.clientY;
    let best = 0;
    cardRefs.current.forEach((el, i) => {
      if (!el) return;
      const rect = el.getBoundingClientRect();
      if (mouseY > rect.top + rect.height / 2) best = i + 1;
    });
    setDropIndex(best);

    // Near the canvas's top or bottom edge, scroll it.
    const container = canvasRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    const edgeZone = 60;
    const topDist = mouseY - rect.top;
    const bottomDist = rect.bottom - mouseY;
    stopScroll();
    if (topDist < edgeZone && container.scrollTop > 0) {
      const speed = Math.round(12 * (1 - topDist / edgeZone));
      const tick = () => {
        container.scrollTop -= speed;
        if (container.scrollTop > 0) scrollRaf.current = requestAnimationFrame(tick);
      };
      scrollRaf.current = requestAnimationFrame(tick);
    } else if (bottomDist < edgeZone && container.scrollTop < container.scrollHeight - container.clientHeight) {
      const speed = Math.round(12 * (1 - bottomDist / edgeZone));
      const tick = () => {
        container.scrollTop += speed;
        if (container.scrollTop < container.scrollHeight - container.clientHeight) scrollRaf.current = requestAnimationFrame(tick);
      };
      scrollRaf.current = requestAnimationFrame(tick);
    }
  }

  function handleDragEnd() {
    setDraggedKey(null);
    setDraggedType(null);
    setDropIndex(null);
    stopScroll();
  }

  function handleCanvasDrop() {
    if (locked) {
      handleDragEnd();
      return;
    }
    if (draggedType !== null && dropIndex !== null) {
      insertField(draggedType, dropIndex);
    } else if (draggedKey !== null && dropIndex !== null) {
      const from = fields.findIndex((f) => f.key === draggedKey);
      const to = dropIndex > from ? dropIndex - 1 : dropIndex;
      if (from >= 0 && to !== from) setFields((prev) => move(prev, from, to));
    }
    handleDragEnd();
  }

  /** The pointer left the canvas: no drop line, so letting go there does nothing. */
  function handleClearIndicator() {
    setDropIndex(null);
    stopScroll();
  }

  // ── Save / close ──────────────────────────────────────────────────────────────
  async function handleSave() {
    if (locked || errors.length > 0 || saving) return;
    setSaving(true);
    try {
      await saveBuilder(form.id, fields, optionGroups);
      toast.success('Survey questions saved successfully');
      onSaved?.();
      await load();
    } catch (err) {
      if (err instanceof SharedOptionGroupError) toast.error(`Shared option group — can't edit here. ${err.message}`);
      else toast.error(getErrorMessage(err, 'Failed to save survey questions'));
    } finally {
      setSaving(false);
    }
  }

  function requestClose() {
    if (saving) return;
    if (dirty && !locked) setConfirmClose(true);
    else onClose();
  }

  const selectedIndex = fields.findIndex((f) => f.key === selectedKey);
  const selected = selectedIndex >= 0 ? fields[selectedIndex] : null;

  const header = (
    <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
      <div className="flex flex-wrap items-center gap-3">
        <div className="min-w-0">
          <h2 className="text-lg font-semibold text-neutral-950">Form Builder</h2>
          <p className="truncate text-sm text-neutral-500">{form.title || 'Untitled form'}</p>
        </div>
        {dirty && !locked && (
          <div className="ml-2 flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 shrink-0 text-blue-700" aria-hidden />
            <span className="text-sm text-blue-700">{summary}</span>
            <Button
              variant="ghost"
              size="xs"
              className="h-7 px-2 text-xs text-gray-500 hover:text-gray-700"
              leftIcon={<RotateCcw className="h-3.5 w-3.5" />}
              aria-label="Revert all changes"
              onClick={revertAll}
            >
              Revert
            </Button>
          </div>
        )}
        {errors.length > 0 && !locked && <ErrorsBadge errors={errors} />}
      </div>
      <div className="flex items-center gap-2">
        {form.directLink && (
          <Button variant="secondary" size="sm" leftIcon={<Eye className="h-4 w-4" />} onClick={() => window.open(previewLinkOnThisHost(form.directLink), '_blank', 'noopener')}>
            Preview
          </Button>
        )}
        {!locked && (
          <Button
            size="sm"
            leftIcon={saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            disabled={errors.length > 0 || saving || loading || loadFailed}
            onClick={() => void handleSave()}
          >
            {saving ? 'Saving...' : 'Save All'}
          </Button>
        )}
      </div>
    </div>
  );

  return (
    <Modal
      isOpen
      onClose={requestClose}
      closeOnEscape={false}
      size="full"
      panelClassName="md:w-[1400px] md:h-[90vh] md:max-h-[90vh]"
      title={header}
      bodyClassName="flex min-h-0 flex-col p-0 md:px-0 lg:flex-row lg:overflow-hidden"
    >
      {loading || saving ? (
        <div className="flex flex-1 items-center justify-center p-10">
          <div className="text-center">
            <Loader2 className="mx-auto mb-3 h-8 w-8 animate-spin text-neutral-500" aria-hidden />
            <p className="text-neutral-500">{saving ? 'Saving form fields...' : 'Loading form fields...'}</p>
          </div>
        </div>
      ) : loadFailed ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-3 p-10 text-center">
          <p className="text-sm text-neutral-500">The form's questions could not be loaded.</p>
          <Button variant="secondary" onClick={() => void load()}>
            Try again
          </Button>
        </div>
      ) : (
        <>
          {!locked && <Palette onDragStart={handlePaletteDragStart} onDragEnd={handleDragEnd} />}
          <Canvas
            locked={locked}
            fields={fields}
            optionGroups={optionGroups}
            merchants={merchants}
            selectedKey={selectedKey}
            errorsByKey={errorsByKey}
            changes={changes}
            draggedKey={draggedKey}
            isDragging={isDragging}
            dropIndex={dropIndex}
            cardRefs={cardRefs}
            canvasRef={canvasRef}
            onSelect={setSelectedKey}
            onCardDragStart={handleCardDragStart}
            onDragOver={handleCanvasDragOver}
            onDrop={handleCanvasDrop}
            onClearIndicator={handleClearIndicator}
            onDragEnd={handleDragEnd}
            onRemove={removeField}
            onRevert={revertField}
          />
          <AdminLockedFieldset>
            <EFormFieldProperties
              field={selected}
              position={selectedIndex + 1}
              fields={fields}
              groups={groups}
              optionGroups={optionGroups}
              demographics={demographics}
              merchants={merchants}
              onChange={updateField}
              onOptionGroupsChange={(next) => {
                if (!locked) setOptionGroups(next);
              }}
            />
          </AdminLockedFieldset>
        </>
      )}

      <ConfirmDialog
        isOpen={confirmClose}
        onClose={() => setConfirmClose(false)}
        onConfirm={onClose}
        title="Discard changes?"
        message="You have unsaved changes. Close without saving them?"
        confirmLabel="Discard changes"
        variant="warning"
      />
    </Modal>
  );
}

// ─── Header pieces ─────────────────────────────────────────────────────────────

function ErrorsBadge({ errors }: { errors: string[] }) {
  return (
    <div className="group relative ml-1">
      <span
        tabIndex={0}
        className="inline-flex cursor-default items-center justify-center rounded-md border border-transparent bg-red-600 px-2 py-0.5 text-[11px] font-medium text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
      >
        {errors.length} {errors.length === 1 ? 'error' : 'errors'}
      </span>
      <div
        role="tooltip"
        className="absolute left-0 top-full z-50 mt-1 hidden w-[calc(100vw-2rem)] max-w-[300px] rounded-md border border-red-200 bg-red-50 p-3 shadow-lg group-focus-within:block group-hover:block"
      >
        <p className="mb-1 text-sm font-medium text-red-700">Required before saving:</p>
        <ul className="list-disc space-y-0.5 pl-4 text-xs text-red-600">
          {errors.map((m) => (
            <li key={m}>{m}</li>
          ))}
        </ul>
      </div>
    </div>
  );
}

// ─── Palette (left) ────────────────────────────────────────────────────────────

function Palette({ onDragStart, onDragEnd }: { onDragStart: (e: ReactDragEvent<HTMLDivElement>, type: number) => void; onDragEnd: () => void }) {
  const [open, setOpen] = useState<Record<string, boolean>>({
    'Basic Fields': true,
    'Choice Fields': true,
    'Special Fields': false,
    'System Fields': false,
  });
  return (
    <div className="flex shrink-0 flex-col border-b border-neutral-200 bg-gray-50/50 lg:w-[220px] lg:overflow-y-auto lg:border-b-0 lg:border-r">
      <div className="border-b border-neutral-200 px-3 py-2.5">
        <p className="text-xs font-semibold uppercase tracking-wider text-neutral-500">Drag to Add</p>
      </div>
      <div className="flex-1 space-y-1 overflow-y-auto p-2">
        {QUESTION_GROUPS.map((g) => (
          <div key={g}>
            <button
              type="button"
              aria-expanded={!!open[g]}
              onClick={() => setOpen((prev) => ({ ...prev, [g]: !prev[g] }))}
              className="flex w-full items-center justify-between px-2 py-1.5 text-xs font-semibold uppercase tracking-wider text-neutral-500 transition-colors hover:text-neutral-950"
            >
              {g}
              <ChevronRight className={cn('h-3.5 w-3.5 transition-transform', open[g] && 'rotate-90')} aria-hidden />
            </button>
            {open[g] && (
              <div className="space-y-0.5 pb-2">
                {QUESTION_TYPES.filter((q) => q.group === g).map((q) => (
                  <div
                    key={q.id}
                    draggable
                    onDragStart={(e) => onDragStart(e, q.id)}
                    onDragEnd={onDragEnd}
                    title={q.description}
                    data-testid="eform-palette-item"
                    className="group flex w-full cursor-grab items-center gap-2.5 rounded-md border border-transparent px-2.5 py-2 text-sm transition-all hover:border-gray-200 hover:bg-white hover:shadow-sm active:cursor-grabbing"
                  >
                    <GripVertical className="h-3.5 w-3.5 shrink-0 text-gray-300 group-hover:text-gray-400" aria-hidden />
                    <QuestionTypeIcon type={q.id} className="h-4 w-4 shrink-0 text-neutral-500 group-hover:text-blue-600" />
                    <span className="truncate text-left">{q.label}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Canvas (centre) ───────────────────────────────────────────────────────────

interface CanvasProps {
  /** A Locked page: the questions show, with no drag, drop, remove or revert. */
  locked: boolean;
  fields: BuilderField[];
  optionGroups: OptionGroup[];
  merchants: Merchant[];
  selectedKey: string | null;
  errorsByKey: Map<string, string[]>;
  changes: { modified: Set<string>; added: Set<string> };
  draggedKey: string | null;
  isDragging: boolean;
  dropIndex: number | null;
  cardRefs: MutableRefObject<(HTMLDivElement | null)[]>;
  canvasRef: MutableRefObject<HTMLDivElement | null>;
  onSelect: (key: string) => void;
  onCardDragStart: (e: ReactDragEvent<HTMLDivElement>, key: string) => void;
  onDragOver: (e: ReactDragEvent<HTMLDivElement>) => void;
  onDrop: () => void;
  onClearIndicator: () => void;
  onDragEnd: () => void;
  onRemove: (key: string) => void;
  onRevert: (key: string) => void;
}

const DROP_LINE = 'pointer-events-none absolute left-2 right-2 z-10 h-0.5 rounded-full bg-blue-500 shadow-[0_0_6px_rgba(59,130,246,0.5)]';

function Canvas({
  locked,
  fields,
  optionGroups,
  merchants,
  selectedKey,
  errorsByKey,
  changes,
  draggedKey,
  isDragging,
  dropIndex,
  cardRefs,
  canvasRef,
  onSelect,
  onCardDragStart,
  onDragOver,
  onDrop,
  onClearIndicator,
  onDragEnd,
  onRemove,
  onRevert,
}: CanvasProps) {
  cardRefs.current.length = fields.length;
  const draggedIndex = draggedKey === null ? null : fields.findIndex((f) => f.key === draggedKey);

  const showLine = (position: number) => {
    if (!isDragging || dropIndex !== position) return false;
    // Moving a card: no line where the drop would leave it in place.
    if (draggedIndex !== null && (position === draggedIndex || position === draggedIndex + 1)) return false;
    return true;
  };

  return (
    <div
      ref={canvasRef}
      data-testid="eform-canvas"
      className="bg-gray-100/60 p-6 lg:min-h-0 lg:flex-1 lg:overflow-y-auto"
      onDragOver={locked ? undefined : onDragOver}
      onDrop={
        locked
          ? undefined
          : (e) => {
              e.preventDefault();
              onDrop();
            }
      }
      onDragLeave={
        locked
          ? undefined
          : (e) => {
              if (!e.currentTarget.contains(e.relatedTarget as Node | null)) onClearIndicator();
            }
      }
    >
      <div className="mx-auto max-w-[640px] space-y-2">
        {fields.length === 0 ? (
          <div className={cn('rounded-xl border-2 border-dashed bg-white p-12 text-center transition-colors', isDragging ? 'border-blue-400 bg-blue-50/30' : 'border-gray-300')}>
            <StickyNote className="mx-auto mb-3 h-10 w-10 text-gray-300" aria-hidden />
            <p className="font-medium text-gray-500">{isDragging ? 'Drop here to add' : 'No fields yet'}</p>
            {!locked && (
              <p className="mt-1 text-sm text-neutral-500">{isDragging ? 'Release to place the field' : 'Drag a field type from the left panel to get started'}</p>
            )}
          </div>
        ) : (
          fields.map((f, i) => (
            <div
              key={f.key}
              ref={(el) => {
                cardRefs.current[i] = el;
              }}
              className="relative"
            >
              {showLine(i) && <div className={cn(DROP_LINE, '-top-[5px]')} data-testid="eform-drop-line" />}
              <FieldCard
                locked={locked}
                field={f}
                position={i + 1}
                optionGroups={optionGroups}
                merchants={merchants}
                isSelected={selectedKey === f.key}
                isDragged={draggedKey === f.key}
                isNew={changes.added.has(f.key)}
                isModified={changes.modified.has(f.key)}
                hasError={(errorsByKey.get(f.key) ?? []).length > 0}
                onSelect={() => onSelect(f.key)}
                onDragStart={(e) => onCardDragStart(e, f.key)}
                onDragEnd={onDragEnd}
                onRemove={() => onRemove(f.key)}
                onRevert={() => onRevert(f.key)}
              />
              {i === fields.length - 1 && showLine(fields.length) && <div className={cn(DROP_LINE, '-bottom-[5px]')} data-testid="eform-drop-line" />}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function addressRequiredLabel(mask: number): string {
  if (mask === 0 || mask === 15) return 'Required';
  return `Req: ${[mask & 1 ? 'Addr' : '', mask & 2 ? 'City' : '', mask & 4 ? 'State' : '', mask & 8 ? 'Zip' : ''].filter(Boolean).join(', ')}`;
}

const CARD_BADGE = 'h-4 px-1 py-0 text-[10px]';

function FieldCard({
  locked,
  field,
  position,
  optionGroups,
  merchants,
  isSelected,
  isDragged,
  isNew,
  isModified,
  hasError,
  onSelect,
  onDragStart,
  onDragEnd,
  onRemove,
  onRevert,
}: {
  locked: boolean;
  field: BuilderField;
  position: number;
  optionGroups: OptionGroup[];
  merchants: Merchant[];
  isSelected: boolean;
  isDragged: boolean;
  isNew: boolean;
  isModified: boolean;
  hasError: boolean;
  onSelect: () => void;
  onDragStart: (e: ReactDragEvent<HTMLDivElement>) => void;
  onDragEnd: () => void;
  onRemove: () => void;
  onRevert: () => void;
}) {
  const label = questionTypeLabel(field.type);
  const isAddress = field.type === ADDRESS;
  const required = isAddress ? field.addressMask > 0 : field.required;

  function handleKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    if (e.target !== e.currentTarget) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onSelect();
    }
  }

  return (
    <div
      draggable={!locked}
      onDragStart={locked ? undefined : onDragStart}
      onDragEnd={locked ? undefined : onDragEnd}
      onClick={onSelect}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={0}
      aria-pressed={isSelected}
      aria-label={`#${position} ${label}`}
      data-field-key={field.key}
      className={cn(
        'group relative cursor-pointer rounded-lg border-2 bg-white transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500',
        hasError
          ? 'border-red-400 bg-red-50/30'
          : isSelected
            ? 'border-green-500 shadow-md shadow-green-100'
            : isNew || isModified
              ? 'border-blue-300 bg-blue-50/20'
              : 'border-gray-200 hover:border-gray-300',
        isDragged && 'scale-[0.98] opacity-40',
      )}
    >
      <div className="flex items-start gap-2 px-3 py-2.5">
        {locked ? (
          <span className="h-4 w-4 shrink-0" aria-hidden />
        ) : (
          <GripVertical className="mt-0.5 h-4 w-4 shrink-0 cursor-grab text-gray-300 opacity-0 transition-opacity active:cursor-grabbing group-hover:opacity-100" aria-hidden />
        )}
        <div className="min-w-0 flex-1">
          <div className="mb-1 flex flex-wrap items-center gap-1.5">
            <QuestionTypeIcon type={field.type} className="h-3.5 w-3.5 shrink-0 text-neutral-500" />
            <span className="text-[11px] font-medium uppercase tracking-wide text-neutral-500">{label}</span>
            {required && (
              <OutlineBadge className={cn(CARD_BADGE, 'border-red-200 text-red-600')}>{isAddress ? addressRequiredLabel(field.addressMask) : 'Required'}</OutlineBadge>
            )}
            {field.showTo === 1 && <OutlineBadge className={cn(CARD_BADGE, 'border-blue-200 text-blue-600')}>Patients</OutlineBadge>}
            {field.showTo === 2 && <OutlineBadge className={cn(CARD_BADGE, 'border-amber-200 text-amber-600')}>Non-Patients</OutlineBadge>}
            {isNew && <span className="ml-0.5 inline-flex items-center rounded-full bg-blue-100 px-1.5 py-0.5 text-[10px] font-medium text-blue-700">new</span>}
            {isModified && (
              <span className="ml-0.5 inline-flex items-center gap-1 rounded-full bg-blue-100 px-1.5 py-0.5 text-[10px] font-medium text-blue-700">
                modified
                <button
                  type="button"
                  title="Revert"
                  aria-label={`Revert question ${position}`}
                  className="hover:text-blue-900"
                  onClick={(e) => {
                    e.stopPropagation();
                    onRevert();
                  }}
                >
                  <RotateCcw className="h-2.5 w-2.5" aria-hidden />
                </button>
              </span>
            )}
            {field.populates && <OutlineBadge className={cn(CARD_BADGE, 'border-purple-200 text-purple-600')}>Populates: {field.populates}</OutlineBadge>}
          </div>
          {/* Question text — content blocks draw their HTML in the preview instead. */}
          {!CONTENT_TYPES.includes(field.type) &&
            (field.content.trim() ? (
              <div className={cn('line-clamp-2 text-sm text-gray-700 [&>*]:m-0', RICH_TEXT)} dangerouslySetInnerHTML={{ __html: field.content }} />
            ) : field.type === PAGE_BREAK ? null : (
              <p className="text-sm italic text-gray-400">No question text</p>
            ))}
        </div>
        {!locked && (
          <button
            type="button"
            aria-label={`Remove question ${position}`}
            onClick={(e) => {
              e.stopPropagation();
              onRemove();
            }}
            className="shrink-0 rounded p-1 text-gray-300 opacity-0 transition-colors hover:bg-red-50 hover:text-red-500 focus:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 group-hover:opacity-100"
          >
            <Trash2 className="h-3.5 w-3.5" aria-hidden />
          </button>
        )}
      </div>

      <div className="pb-1 pl-9 pr-4 pt-0.5">
        <FieldPreview field={field} optionGroups={optionGroups} />
      </div>

      {field.type === CREDIT_CARD
        ? field.instruction.trim() && (
            <div className="pb-3 pl-9 pr-4">
              <span className="text-xs text-gray-500">
                Merchant: {merchants.find((m) => String(m.merchantId) === field.instruction)?.friendlyTitle || field.instruction}
              </span>
            </div>
          )
        : field.instruction.trim() && (
            <div className="pb-3 pl-9 pr-4">
              <div className={cn('text-xs text-gray-500 [&_img]:rounded', RICH_TEXT_BLOCK)} dangerouslySetInnerHTML={{ __html: field.instruction }} />
            </div>
          )}
    </div>
  );
}

// ─── Field previews (as the field looks on the form) ───────────────────────────

const BOX = 'flex items-center rounded border border-gray-200 bg-gray-50 px-2 text-xs text-gray-400';
const CONTENT_HTML = cn('text-sm leading-relaxed text-gray-700', RICH_TEXT_BLOCK);

function FieldPreview({ field, optionGroups }: { field: BuilderField; optionGroups: OptionGroup[] }) {
  const options = fieldOptions(field, optionGroups);
  switch (field.type) {
    case 10:
      return <div className={cn(BOX, 'h-8')}>Short text answer</div>;
    case 9:
      return <div className="h-16 rounded border border-gray-200 bg-gray-50 px-2 pt-1.5 text-xs text-gray-400">Long text answer</div>;
    case 18:
      return (
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <div className={cn(BOX, 'h-8')}>First Name</div>
          <div className={cn(BOX, 'h-8')}>Last Name</div>
        </div>
      );
    case 14:
      return (
        <div className="flex items-center gap-1.5">
          <div className={cn(BOX, 'h-8 flex-1')}>Month</div>
          <div className={cn(BOX, 'h-8 flex-1')}>Day</div>
          <div className={cn(BOX, 'h-8 flex-1')}>Year</div>
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded border border-gray-200 bg-gray-50">
            <Calendar className="h-3.5 w-3.5 text-gray-400" aria-hidden />
          </div>
        </div>
      );
    case ADDRESS:
      return (
        <div className="space-y-1.5">
          <div className={cn(BOX, 'h-7')}>Street Address</div>
          <div className={cn(BOX, 'h-7')}>Street Address Line 2</div>
          <div className="flex gap-1.5">
            <div className={cn(BOX, 'h-7 flex-[2]')}>City</div>
            <div className={cn(BOX, 'h-7 flex-[1.5]')}>State</div>
            <div className={cn(BOX, 'h-7 flex-1')}>Zip</div>
          </div>
          <div className={cn(BOX, 'h-7')}>Country</div>
        </div>
      );
    case 7:
    case 8: {
      const round = field.type === 7;
      return (
        <div className="space-y-1">
          {!round && field.checkAll && (
            <div className="flex items-center gap-2 text-sm">
              <div className="h-3.5 w-3.5 shrink-0 rounded-sm border-2 border-blue-400 bg-blue-50" />
              <span className="text-xs font-medium text-blue-600">Check All</span>
            </div>
          )}
          {options.length > 0 ? (
            options.map((o, i) => (
              <div key={o.id ?? `i${i}`} className="flex items-center gap-2 text-sm">
                <div className={cn('h-3.5 w-3.5 shrink-0 border-2 border-gray-300', round ? 'rounded-full' : 'rounded-sm')} />
                <span className="text-xs text-gray-600">{o.text || `Option ${i + 1}`}</span>
              </div>
            ))
          ) : (
            <p className="text-xs italic text-neutral-500">No options added</p>
          )}
          {field.addOther && (
            <div className="flex items-center gap-2 text-sm">
              <div className={cn('h-3.5 w-3.5 shrink-0 border-2 border-gray-300', round ? 'rounded-full' : 'rounded-sm')} />
              <span className="text-xs italic text-gray-400">Other...</span>
            </div>
          )}
        </div>
      );
    }
    case 16:
      return (
        <div className={cn(BOX, 'h-8 justify-between')}>
          <span>{options.length > 0 ? `${options.length} options` : 'No options added'}</span>
          <ChevronDown className="h-3 w-3" aria-hidden />
        </div>
      );
    case 11:
      return (
        <div className="flex h-20 items-center justify-center rounded border border-gray-200 bg-gray-50">
          <PenTool className="h-5 w-5 text-gray-300" aria-hidden />
          <span className="ml-1.5 text-xs text-gray-400">Signature pad</span>
        </div>
      );
    case 12:
      return (
        <div className="flex h-12 items-center justify-center rounded border-2 border-dashed border-gray-200 bg-gray-50">
          <FileUp className="h-4 w-4 text-gray-300" aria-hidden />
          <span className="ml-1.5 text-xs text-gray-400">Drag file or click to upload</span>
        </div>
      );
    case PAGE_BREAK:
      return (
        <div className="flex items-center gap-2 py-1">
          <div className="flex h-7 flex-1 items-center justify-center rounded border border-gray-200 bg-gray-50 text-xs text-gray-500">&lt; Previous</div>
          <div className="flex h-7 flex-1 items-center justify-center rounded border border-gray-200 bg-gray-50 text-xs text-gray-500">Next &gt;</div>
        </div>
      );
    case 19:
      return (
        <div className="rounded border border-gray-200 p-2">
          {field.content ? <div className={CONTENT_HTML} dangerouslySetInnerHTML={{ __html: field.content }} /> : <span className="text-xs italic text-gray-400">Content block</span>}
        </div>
      );
    case 23:
      return (
        <div className="max-h-[150px] overflow-y-auto rounded border border-gray-200 p-2">
          {field.content ? <div className={CONTENT_HTML} dangerouslySetInnerHTML={{ __html: field.content }} /> : <span className="text-xs italic text-gray-400">Content block (scroll)</span>}
        </div>
      );
    case 28:
      return (
        <div className="space-y-2">
          <div className="max-h-[120px] overflow-y-auto rounded border border-gray-200 p-2">
            {field.content ? (
              <div className={CONTENT_HTML} dangerouslySetInnerHTML={{ __html: field.content }} />
            ) : (
              <span className="text-xs italic text-gray-400">Scrollable content (terms, policies, etc.)</span>
            )}
          </div>
          <div className="flex items-center gap-2 text-sm">
            <div className="h-3.5 w-3.5 shrink-0 rounded-sm border-2 border-gray-300 opacity-50" />
            <span className="text-xs text-gray-500">{plainText(field.instruction) || 'I have read and accept the above'}</span>
          </div>
        </div>
      );
    case 15:
      return <div className="flex h-6 items-center rounded border border-gray-200 bg-gray-100 px-2 text-[11px] italic text-gray-400">Hidden field (not visible to user)</div>;
    case 24:
      return <div className={cn(BOX, 'h-8')}>Today&apos;s date (auto-filled)</div>;
    case 26:
      return <div className="flex h-6 items-center rounded border border-gray-200 bg-gray-100 px-2 text-[11px] italic text-gray-400">Client IP (auto-captured)</div>;
    case 25:
      return <div className="h-16 rounded border border-gray-200 bg-gray-50 px-2 pt-1.5 text-xs text-gray-400">Patient note</div>;
    case 17:
      return (
        <div className="space-y-1.5">
          <div>
            <span className="text-[11px] font-medium text-gray-500">Height</span>
            <div className={cn(BOX, 'mt-0.5 h-7 justify-between')}>
              <span>Select height...</span>
              <ChevronDown className="h-3 w-3" aria-hidden />
            </div>
          </div>
          <div>
            <span className="text-[11px] font-medium text-gray-500">Weight (lbs.)</span>
            <div className={cn(BOX, 'mt-0.5 h-7')}>Enter weight</div>
          </div>
          <div className="flex h-6 items-center rounded border border-gray-200 bg-gray-100 px-2 text-[11px] italic text-gray-400">BMI (auto-calculated)</div>
        </div>
      );
    case 27:
      return (
        <div className="space-y-2">
          {[
            ['Transactional', 'Confirmations, receipts, notifications'],
            ['Marketing', 'Promotions, newsletters, campaigns'],
          ].map(([title, hint]) => (
            <div key={title}>
              <div className="mb-1 flex items-baseline gap-1">
                <span className="text-[11px] font-semibold text-gray-700">{title}</span>
                <span className="text-[10px] text-gray-400">– {hint}</span>
              </div>
              <div className="flex items-center gap-4">
                {['Email', 'SMS'].map((ch) => (
                  <div key={ch} className="flex items-center gap-1.5">
                    <div className="relative h-3.5 w-6 rounded-full bg-blue-500">
                      <div className="absolute right-0.5 top-0.5 h-2.5 w-2.5 rounded-full bg-white" />
                    </div>
                    <span className="text-[10px] text-gray-500">{ch}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      );
    case 20:
      return (
        <div className="flex justify-center">
          <div className="rounded bg-blue-600 px-6 py-2 text-xs font-medium text-white">Submit</div>
        </div>
      );
    case CREDIT_CARD:
      return (
        <div className="space-y-1.5">
          <div className={cn(BOX, 'h-7')}>Card Number</div>
          <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
            <div className={cn(BOX, 'h-7')}>Exp Date</div>
            <div className={cn(BOX, 'h-7')}>CVV</div>
          </div>
        </div>
      );
    default:
      return <div className={cn(BOX, 'h-8')}>Select a field type</div>;
  }
}
